/**
 * Cold-load auth bootstrap. Reads persisted tokens/user from localStorage and
 * re-establishes the in-memory session: local JWT validity check first (no
 * network), background profile refresh, or refresh-token exchange. Kept as a
 * plain function (called from a mount effect) so it stays out of the provider's
 * render function and under the size/complexity gates. Reads `auth_tokens`
 * directly — this IS the auth bootstrap the lint guard carves out.
 */
import { isJwtValid } from './AuthContext';
import type { AuthTokens, User } from './AuthContext';
import { fetchUserProfileResponse } from './authApi';
import { logger } from '@/utils/debugLogger';

interface AuthBootstrapHandlers {
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  setIsLoading: (loading: boolean) => void;
  refreshTokens: (refreshToken: string) => Promise<AuthTokens>;
  logout: () => void;
}

export function runAuthBootstrap({
  setUser,
  setTokens,
  setIsLoading,
  refreshTokens,
  logout,
}: AuthBootstrapHandlers): void {
  const storedTokens = localStorage.getItem('auth_tokens');
  const storedUser = localStorage.getItem('auth_user');

  if (!storedTokens || !storedUser) {
    // No stored tokens — not authenticated
    setIsLoading(false);
    return;
  }

  try {
    const parsedTokens = JSON.parse(storedTokens) as AuthTokens;
    const parsedUser = JSON.parse(storedUser) as User;

    setUser(parsedUser);

    // Decode the access JWT locally instead of making a network round-trip.
    // Server-side revocation is handled by the 401 interceptor in client.ts
    // on the first protected API call.
    if (isJwtValid(parsedTokens.access)) {
      // Token locally valid — publish it, then unblock render immediately;
      // profile refresh continues in background. Publishing the token ONLY in
      // this valid branch is what prevents the cold-load 401: an expired token
      // must never reach state, or the token-gated bootstrap query would fire
      // with it and the browser would log a 401 (network-level, unsuppressible).
      setTokens(parsedTokens);
      setIsLoading(false);
      fetchUserProfileResponse(parsedTokens.access)
        .then(res => {
          if (res.status === 401) {
            // Token was revoked server-side: attempt refresh; logout if that also fails
            refreshTokens(parsedTokens.refresh).catch(() => logout());
            return null;
          }
          return res.ok ? res.json() : null;
        })
        .then(data => {
          if (data) {
            setUser(data);
            localStorage.setItem('auth_user', JSON.stringify(data));
          }
        })
        .catch(() => {
          // Degraded-mode: cold load reuses the previously-cached user from
          // localStorage when the profile endpoint is unreachable (e.g. offline).
          // This is NOT a synthetic fallback — the cached user was written by a
          // prior successful login that did fetch the real profile. Login (above)
          // strictly requires a fresh profile and will not write a fake one.
        });
    } else {
      // Token locally expired, attempt refresh
      refreshTokens(parsedTokens.refresh)
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          // Refresh failed, clear auth
          logout();
          setIsLoading(false);
        });
    }
  } catch (error) {
    logger.error(
      { component: 'AuthProvider', action: 'bootstrap' },
      'Error parsing stored auth data',
      error,
    );
    logout();
    setIsLoading(false);
  }
}
